import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useAuth } from "../../../../../contexts/AuthContext";
import { useCallBacks } from "../../../../../data/useCallQueue";
import useWilmaUsers from "../../../../../data/useWilmaUsers";
import classNames from "../../../../../utils/classNames";
import { vehicleTitle } from "../../../../../utils/callQueue";
import PriceFormat from "../../../../../utils/priceFormat";
import RegPlate from "../../../../shared/RegPlate";
import Pill from "../Pill";

// Every open call back. They're served from the live queue when due; this is the list to look ahead.
export default function CallBacksList() {
  const { t } = useTranslation("leads");
  const { userId } = useAuth();
  const [mineOnly, setMineOnly] = useState(true);
  const { callBacks } = useCallBacks(mineOnly, "CallBacksList");
  const { wilmaUsers } = useWilmaUsers();

  const names = useMemo(() => {
    const map = new Map<string, string>();
    (wilmaUsers.data?.data ?? []).forEach((user) => map.set(user.id, user.fullname || user.forename || ""));
    return map;
  }, [wilmaUsers.data]);

  const rows = callBacks.data?.data ?? [];

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
        <h2 className="text-[15px] font-semibold text-gray-900">{t("Call backs")}</h2>
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
          {[true, false].map((value) => (
            <button
              key={String(value)}
              type="button"
              onClick={() => setMineOnly(value)}
              aria-pressed={mineOnly === value}
              className={classNames(
                "rounded-md px-3 py-1.5 text-[13px] font-medium",
                mineOnly === value ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
              )}
            >
              {value ? t("Mine") : t("Everyone's")}
            </button>
          ))}
        </div>
      </div>

      {callBacks.isLoading ? (
        <p className="px-4 py-8 text-center text-sm text-gray-500">{t("Loading call backs…")}</p>
      ) : rows.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-gray-500">{t("No call backs booked.")}</p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {rows.map((row) => {
            const due = new Date(row.dueAt);
            const owner = row.assignedUserId ? (row.assignedUserId === userId ? t("you") : names.get(row.assignedUserId)) : null;
            return (
              <li key={row.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <RegPlate vrm={row.vrm} />
                    <Pill tone={row.isDue ? "amber" : "gray"}>
                      {row.isDue ? t("Due") : t("Booked")}{" "}
                      {due.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </Pill>
                    {owner ? <Pill tone="slate">{t("Kept by {{name}}", { name: owner })}</Pill> : <Pill tone="slate">{t("Pool")}</Pill>}
                  </span>
                  <span className="text-[13px] font-semibold text-gray-900">{vehicleTitle(null, row.make, row.model) || t("Unknown vehicle")}</span>
                  <span className="text-xs text-gray-600">
                    {row.contactName} · {row.telephone} · {t("Quoted")} {PriceFormat(row.quotedPrice)}
                  </span>
                  {row.note && <span className="text-xs text-gray-700">{row.note}</span>}
                </div>
                <Link to={`/bookings/${row.leadId}`} className="text-[13px] font-medium text-primary-700 underline">
                  {t("Open lead")}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
